/**
 * BusinessEnvironmentFactor Validation Schemas
 */

import { z } from 'zod';
import { nameField, optionalBoundedTextOrEmpty } from './shared';

/**
 * Common validation rules for BusinessEnvironmentFactor entities
 */
export const businessEnvironmentFactorValidationRules = {
  /** BusinessEnvironmentFactor name validation (1-200 characters) */
  name: nameField(200, 'Name is required', 'Name must be less than 200 characters'),

  /** Description validation (optional, max 2000 characters) */
  description: optionalBoundedTextOrEmpty(2000, 'Description must be less than 2000 characters'),

  /** Category validation (optional string) */
  category: optionalBoundedTextOrEmpty(200, 'Category must be less than 200 characters'),

  /** Label validation (optional string) */
  label: optionalBoundedTextOrEmpty(200, 'Label must be less than 200 characters'),

  /** Notes validation (optional, max 5000 characters) */
  notes: optionalBoundedTextOrEmpty(5000, 'Notes must be less than 5000 characters'),
};

/**
 * Full BusinessEnvironmentFactor create form schema
 */
export const createBusinessEnvironmentFactorSchema = z.object({
  name: businessEnvironmentFactorValidationRules.name,
  description: businessEnvironmentFactorValidationRules.description,
  category: businessEnvironmentFactorValidationRules.category,
  label: businessEnvironmentFactorValidationRules.label,
  tags: z.array(z.string().min(1)).optional().default([]),
  notes: businessEnvironmentFactorValidationRules.notes,
});

/**
 * BusinessEnvironmentFactor update form schema (at least one field must be present)
 */
export const updateBusinessEnvironmentFactorSchema = z
  .object({
    name: businessEnvironmentFactorValidationRules.name.optional(),
    description: businessEnvironmentFactorValidationRules.description,
    category: businessEnvironmentFactorValidationRules.category,
    label: businessEnvironmentFactorValidationRules.label,
    tags: z.array(z.string().min(1)).optional(),
    notes: businessEnvironmentFactorValidationRules.notes,
  })
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

/**
 * Type inference for create business environment factor form
 */
export type CreateBusinessEnvironmentFactorFormData = z.infer<
  typeof createBusinessEnvironmentFactorSchema
>;

/**
 * Type inference for update business environment factor form
 */
export type UpdateBusinessEnvironmentFactorFormData = z.infer<
  typeof updateBusinessEnvironmentFactorSchema
>;
